/**
 * Broadcast `planos:change` — mudança em plano de ação (C-24).
 * ------------------------------------------------------------
 * Mesmo transporte do `tarp:critical` (ver `tarp-realtime.ts`): o datasource é o
 * choke point das escritas, então emitir aqui cobre criar plano e mudar ação. Quem
 * assina (o sino, em qualquer tela) invalida `['planos']` e vê a mudança feita por
 * outro usuário na hora, sem esperar o polling.
 *
 * Tópico próprio, não o `tarp:critical`: plano não é `tarp_records`, e a regra #3 e
 * o hardening futuro por trigger no DB tratam aquele tópico como contrato de TARP.
 */
import { supabase } from '@/lib/supabase';

export const PLANOS_CHANNEL = 'planos:change';

export interface PlanoAlteradoPayload {
  event: 'create' | 'update';
  id: string;
}

type Listener = (payload: PlanoAlteradoPayload) => void;

const listeners = new Set<Listener>();
let sharedChannel: ReturnType<typeof supabase.channel> | null = null;

/** Um canal só para emitir e receber; `self: false` evita o eco no próprio emissor. */
function getSharedChannel() {
  if (!sharedChannel) {
    const channel = supabase.channel(PLANOS_CHANNEL, {
      config: { broadcast: { self: false } },
    });
    channel.on('broadcast', { event: 'change' }, ({ payload }) => {
      for (const listener of listeners) listener(payload as PlanoAlteradoPayload);
    });
    channel.subscribe();
    sharedChannel = channel;
  }
  return sharedChannel;
}

/**
 * Best-effort: o plano já foi persistido; o broadcast só antecipa o refresh dos outros
 * clientes (o polling segue como rede de segurança). Não bloqueia nem lança.
 */
export function emitPlanoAlterado(payload: PlanoAlteradoPayload): void {
  void getSharedChannel().send({ type: 'broadcast', event: 'change', payload });
}

/** Assina o canal; chama `onChange` a cada broadcast. Retorna unsubscribe. */
export function subscribePlanos(onChange: Listener): () => void {
  getSharedChannel();
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}
